import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { cn } from '../../lib/utils';

// Promise-based confirm: `const confirm = useConfirm(); if (await confirm({...}))`.
// One AlertDialog for the whole app — replaces window.confirm.
const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Are you sure?',
  description: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  destructive: false,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, ...DEFAULTS });
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ open: true, ...DEFAULTS, ...opts });
    });
  }, []);

  const settle = useCallback((value) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) {
      resolver.current(value);
      resolver.current = null;
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(open) => { if (!open) settle(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            {state.description ? (
              <AlertDialogDescription>{state.description}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>{state.cancelText}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={cn(state.destructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90')}
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}
